import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import facade from "../services/qwof/apiFacade";
import NavigationRow from "../components/NavigationRow.jsx";
import Spinner from "../components/Spinner.jsx";
import QuagganEmoji from "../components/QuagganEmoji.jsx";

function EyeIcon({ crossed = false }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2 12C3.8 8.7 7.1 6.5 12 6.5C16.9 6.5 20.2 8.7 22 12C20.2 15.3 16.9 17.5 12 17.5C7.1 17.5 3.8 15.3 2 12Z"
        stroke="white"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="white" strokeWidth="1.8" />
      {crossed && (
        <line
          x1="4"
          y1="18"
          x2="20"
          y2="6"
          stroke="white"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="9"
        y="3"
        width="11"
        height="14"
        rx="2"
        stroke="white"
        strokeWidth="1.8"
      />
      <rect
        x="4"
        y="7"
        width="11"
        height="14"
        rx="2"
        stroke="white"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function UserPage() {
  const { username: loggedInUsername, isLoggedIn } = useAuth();
  const { username: routeUsername } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [nickname, setNickname] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [apiKeySaved, setApiKeySaved] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKeyBusy, setApiKeyBusy] = useState(false);
  const [apiKeyBusyMessage, setApiKeyBusyMessage] = useState("");
  const [copied, setCopied] = useState(false);

  // Guard: must be logged in and only view your own page
  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/");
      return;
    }
    if (
      routeUsername &&
      loggedInUsername &&
      routeUsername !== loggedInUsername
    ) {
      navigate("/");
    }
  }, [isLoggedIn, loggedInUsername, routeUsername, navigate]);

  useEffect(() => {
    if (!isLoggedIn || !loggedInUsername || routeUsername !== loggedInUsername)
      return;

    const loadUser = async () => {
      setLoading(true);
      setError("");
      setMessage("");
      const start = Date.now();
      try {
        const data = await facade.getUser(loggedInUsername);
        if (data) {
          if (data.nickname) {
            setNickname(data.nickname);
          }
          if (data.gw2ApiKey) {
            setApiKeySaved(true);
          }
        }
      } catch (err) {
        console.error("Failed to load user", err);
        setError("Failed to load user profile.");
      } finally {
        const elapsed = Date.now() - start;
        const remaining = 500 - elapsed;
        if (remaining > 0) {
          setTimeout(() => {
            setLoading(false);
          }, remaining);
        } else {
          setLoading(false);
        }
      }
    };

    loadUser();
  }, [isLoggedIn, loggedInUsername, routeUsername]);

  const handleSaveNickname = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    try {
      await facade.setNickname(loggedInUsername, nickname);
      setMessage("Nickname updated.");
    } catch (err) {
      console.error("Failed to set nickname", err);
      setError("Failed to update nickname.");
    }
  };

  const handleSaveApiKey = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setApiKeyBusy(true);
    setApiKeyBusyMessage("Setting API key...");
    const start = Date.now();
    try {
      await facade.setApiKey(apiKey);
      setApiKeySaved(true);
      setApiKey("");
      setMessage("GW2 API key saved.");
    } catch (err) {
      console.error("Failed to set API key", err);
      setError(
        `Failed to save GW2 API key: ${err.fullError || err.message || "Unknown error"}`
      );
    } finally {
      const elapsed = Date.now() - start;
      const remaining = 1000 - elapsed;
      if (remaining > 0) {
        setTimeout(() => {
          setApiKeyBusy(false);
          setApiKeyBusyMessage("");
        }, remaining);
      } else {
        setApiKeyBusy(false);
        setApiKeyBusyMessage("");
      }
    }
  };

  const handleDeleteApiKey = async () => {
    setError("");
    setMessage("");
    setApiKeyBusy(true);
    setApiKeyBusyMessage("Deleting API key...");
    const start = Date.now();
    try {
      await facade.deleteApiKey();
      setApiKeySaved(false);
      setApiKey("");
      setMessage("GW2 API key deleted.");
    } catch (err) {
      console.error("Failed to delete API key", err);
      setError(
        `Failed to delete GW2 API key: ${err.fullError || err.message || "Unknown error"}`
      );
    } finally {
      const elapsed = Date.now() - start;
      const remaining = 1000 - elapsed;
      if (remaining > 0) {
        setTimeout(() => {
          setApiKeyBusy(false);
          setApiKeyBusyMessage("");
        }, remaining);
      } else {
        setApiKeyBusy(false);
        setApiKeyBusyMessage("");
      }
    }
  };

  const handleCopyApiKey = async () => {
    if (!apiKey) return;
    try {
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(apiKey);
        setCopied(true);
        setError("");
        // Clear the copied hint after a short delay
        setTimeout(() => setCopied(false), 2000);
      } else {
        setError("Clipboard not available in this browser.");
      }
    } catch (err) {
      console.error("Failed to copy API key", err);
      setError("Failed to copy GW2 API key.");
    }
  };

  if (!isLoggedIn) {
    return null;
  }

  return (
    <div className="register-route">
      <NavigationRow />
      <div className="register-route-inner">
        <div className="register-panel">
          <h2 className="register-title">User: {loggedInUsername}</h2>

          {loading && (
            <>
              <Spinner />
              <p className="login-help-text" style={{ textAlign: "center" }}>
                Loading user profile
              </p>
            </>
          )}
          {error && (
            <>
              <p className="register-error">{error}</p>
              <QuagganEmoji type="failure" />
            </>
          )}
          {message && !error && (
            <>
              <p className="login-help-text">{message}</p>
              <QuagganEmoji type="success" />
            </>
          )}

          {!loading && (
            <>
              <section style={{ marginTop: 16 }}>
                <h3 style={{ marginBottom: 8 }}>Nickname</h3>
                <form onSubmit={handleSaveNickname}>
                  <input
                    type="text"
                    className="login-input"
                    placeholder="Nickname"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    style={{ marginBottom: 15 }}
                  />
                  <button type="submit" className="login-btn">
                    Save nickname
                  </button>
                </form>
              </section>

              <section style={{ marginTop: 24 }}>
                <h3 style={{ marginBottom: 8 }}>Guild Wars 2 API key</h3>
                <p
                  className="login-help-text"
                  style={{
                    marginBottom: 8,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      padding: "2px 10px",
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                      backgroundColor: apiKeySaved ? "#2e7d32" : "#b71c1c",
                      color: "#fff",
                    }}
                  >
                    {apiKeySaved ? "SAVED" : "NO KEY"}
                  </span>
                  {apiKeySaved
                    ? "A GW2 API key is currently configured for your account."
                    : "No GW2 API key is configured yet."}
                </p>
                {apiKeyBusy && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 8,
                    }}
                  >
                    <Spinner />
                    <span className="login-help-text">{apiKeyBusyMessage}</span>
                  </div>
                )}
                <form
                  onSubmit={handleSaveApiKey}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type={showApiKey ? "text" : "password"}
                      className="login-input"
                      placeholder={
                        apiKeySaved ? "API key is saved" : "Paste GW2 API key"
                      }
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      style={{ paddingRight: 100 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey((prev) => !prev)}
                      style={{
                        position: "absolute",
                        right: 40,
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        background: "transparent",
                        border: "none",
                        padding: 0,
                      }}
                      aria-label={showApiKey ? "Hide API key" : "Show API key"}
                    >
                      <EyeIcon crossed={showApiKey} />
                    </button>
                    <button
                      type="button"
                      onClick={handleCopyApiKey}
                      disabled={!apiKey}
                      style={{
                        position: "absolute",
                        right: 10,
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: apiKey ? "pointer" : "not-allowed",
                        background: "transparent",
                        border: "none",
                        padding: 0,
                      }}
                      aria-label="Copy API key"
                    >
                      <CopyIcon />
                    </button>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      type="submit"
                      className="login-btn"
                      disabled={!apiKey}
                    >
                      Save API key
                    </button>
                    <button
                      type="button"
                      className="login-btn"
                      onClick={handleDeleteApiKey}
                      disabled={!apiKeySaved}
                    >
                      Delete API key
                    </button>
                  </div>
                  <p className="login-help-text" style={{ marginTop: 8 }}>
                    {apiKeySaved
                      ? "Your key is already saved. Paste a new key to update it."
                      : "Paste your GW2 API key here. It is stored server-side and never displayed."}
                  </p>
                </form>
                <p className="login-help-text" style={{ marginTop: 8 }}>
                  Make sure your GW2 API key has
                  {" "}
                  <span
                    style={{
                      fontWeight: 800,
                      textTransform: "uppercase",
                      color: "#ffd54f",
                    }}
                  >
                    ACCOUNT
                  </span>
                  {" "}
                  and
                  {" "}
                  <span
                    style={{
                      fontWeight: 800,
                      textTransform: "uppercase",
                      color: "#ffb74d",
                    }}
                  >
                    GUILDS
                  </span>
                  {" "}
                  permissions enabled. You can create one on the official GW2 site
                  {" "}
                  <a
                    href="https://account.arena.net/applications/create"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#7fd0ff", textDecoration: "underline" }}
                  >
                    here
                  </a>
                  .
                </p>
              </section>
            </>
          )}
        </div>
      </div>
      {copied && (
        <div
          style={{
            position: "fixed",
            left: "50%",
            bottom: 32,
            transform: "translateX(-50%)",
            background: "#222",
            color: "#fff",
            padding: "10px 24px",
            borderRadius: 8,
            fontWeight: "bold",
            fontSize: 16,
            zIndex: 9999,
            boxShadow: "0 2px 8px #0008",
            pointerEvents: "none",
          }}
        >
          API key copied to clipboard
        </div>
      )}
    </div>
  );
}

export default UserPage;
